import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components/macro";
import { useNavigate } from "react-router-dom";
import { ResponseT } from "@utils/api/base";
import useSubmissions from "./useSubmissions";
import { Submission } from "../../type";
import { FlexRow } from '@components/layout/FlexStyles';
import MenuButton from '@components/controls/MenuButton';
import BackArrowIcon from '@components/icons/backArrow.icon';
import DisplayDetails from '@components/submissions/components/DisplayDetails';
import DisplayPending from '@components/submissions/components/DisplayPending';

interface PropsI {
  hash?: string;
}

const debug = false;
const SubmissionsDetails = (props:PropsI) => {
  const {hash} = props;
  const [refreshData, setRefreshData] = useState<boolean>(false);
  const { getSubmission } = useSubmissions();

  const navigate = useNavigate();
  const urlOrigin = window.location.origin;
  debug && console.log('SubmissionDetails url:', urlOrigin);

  // ###########################################################################
  // DATA LOADING
  // ###########################################################################
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadData = React.useCallback(async () => {
    debug && console.log("loadData");
    setLoading(true);
    let res: ResponseT<Submission>;

    res = await getSubmission(hash);

    setData(res);
    setLoading(false);
    // setLoadedAll(!res.continues);
  }, []);

  useEffect(() => {
    if (loading === false) {
      loadData().then();
    }
  }, []);

  // ###########################################################################
  // HANDLE REFRESH OF DATA IF STATUS IS PENDING
  // ###########################################################################

  // Load data on if pending
  useEffect(() => {
    if (data && loading === false) {
      setRefreshData(false);
      loadData().then();
    }
  }, [refreshData]);

  const handleAutoRefreshIfPending = () => {
    if (data?.status === 'pending' && !loading) {
      setRefreshData(true);
    }
  };

  // Set up an interval to refresh data every 5 seconds if there are pending submissions
  useEffect(() => {
    // Set up an interval to refresh data every 5 seconds (for example)
    const intervalId = setInterval(handleAutoRefreshIfPending, 10000);
    // Clean up the interval when the component is unmounted
    return () => clearInterval(intervalId);
  }, [data, loading]);

  const adjustHeight = data?.status === 'pending' ? 200 : 200;

  // ###########################################################################
  // Interactions
  // ###########################################################################
  debug && console.log(data);
  return (
    <Container height={adjustHeight}>
      {data != null && !loading ? (
        <Content>
          <FlexRow v_centered>
            <MenuButton
              text={'Back to List'}
              tooltipPlacement={'bottom'}
              onClick={()=>{navigate('/submissions')}}
              icon={<BackArrowIcon size={32} />}
              width={220}
              height={40}
            />
            <Title>Submission Details</Title>
          </FlexRow>
          { (data.status === 'pending') ? <DisplayPending /> : null }
          <DisplayDetails data={data}/>
        </Content>
      ) : (
        <>
          {loading ? (
            <Loader />
          ) : (
            <Error>
              Missing Submission or the filter settings did not return any
              submissions
            </Error>
          )}
        </>
      )}
    </Container>
  );
};

export default SubmissionsDetails;

interface StyledExtraProps {
  small?: boolean;
}

const Container = styled.div<{height?:number}>`
  height: calc(100vh - 100px - ${(p) => p.height}px);
  width: calc(100vw - 58px);
  padding-left: 5px;
  background-color: ${(p) => p.theme.palette.backgroundPrimary};
`;
Container.defaultProps = {
  height: 0
}

const Content = styled.div`
  display: flex;
  flex-direction: column;
  margin: 20px;
`;

const Error = styled.div`
  padding: 10px;
  color: ${(p) => p.theme.palette.titleSecondary};
  font-size: calc(0.8 * ${(props) => props.theme.sizes.default});
`;



const Title = styled.div`
  padding: 0 50px 5px 5px;
  font-size: ${(props) => props.theme.sizes.default};
  color: ${(props) => props.theme.palette.accentPrimary};
`;

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
`;

const Loader = styled.div<StyledExtraProps>`
  display: inline-block;
  border-radius: 100%;
  margin: 2px;
  border: ${(p) => p.theme.borders.default};
  border-bottom-color: transparent;
  width: ${(props) => (props.small ? 12 : 22)}px;
  height: ${(props) => (props.small ? 12 : 22)}px;
  animation: ${rotate} 0.75s linear infinite;
  background-color: ${(p) => p.theme.palette.backgroundPrimary};
`;


